/**
 * ErrorBoundary Components
 *
 * Provides graceful error handling for feature modules and chart components.
 * Catches rendering errors and displays friendly recovery UI.
 *
 * Features:
 * - Graceful error catching that prevents app crashes
 * - User-friendly error messages
 * - "Report Issue" functionality for user feedback
 * - Integration with logging service for debugging
 */

import * as React from 'react';
import { Button } from './Button';
import { Card } from './Card';
import { createLogger, generateCorrelationId } from '@/services/loggingService';
import { captureErrorBoundaryError } from '@/services/errorMonitoringService';

// =============================================================================
// Types
// =============================================================================

export interface FeatureErrorBoundaryProps {
  /** Feature name for error context */
  feature: string;
  /** Custom fallback UI (optional) */
  fallback?: React.ReactNode;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Children to render */
  children: React.ReactNode;
}

export interface ChartErrorBoundaryProps {
  /** Chart name for error context */
  chartName?: string;
  /** Chart height for fallback sizing */
  height?: number;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Children to render */
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string | null;
  isReportModalOpen: boolean;
}

// =============================================================================
// Icons
// =============================================================================

const AlertTriangleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ChartErrorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
    <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />
  </svg>
);

const BugIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 116 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0112 0v3c0 3.3-2.7 6-6 6z" />
    <path d="M12 20v-9M6.53 9C4.6 8.8 3 7.1 3 5M6 13H3M6 17H3M21 5c0 2.1-1.6 3.8-3.53 4M18 13h3M18 17h3" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Create logger instance for error boundary
const logger = createLogger('ErrorBoundary');

// =============================================================================
// Error Report Types
// =============================================================================

export interface ErrorReport {
  errorId: string;
  timestamp: string;
  feature: string;
  errorMessage: string;
  errorStack?: string;
  componentStack?: string;
  userDescription?: string;
  userAgent: string;
  url: string;
}

// =============================================================================
// Report Issue Modal
// =============================================================================

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  feature: string;
  errorId: string;
}

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  isOpen,
  onClose,
  error,
  errorInfo,
  feature,
  errorId,
}) => {
  const [description, setDescription] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const report: ErrorReport = {
      errorId,
      timestamp: new Date().toISOString(),
      feature,
      errorMessage: error?.message || 'Unknown error',
      errorStack: error?.stack,
      componentStack: errorInfo?.componentStack,
      userDescription: description.trim() || undefined,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Log the error report
    logger.error('User submitted error report', {
      report,
      hasUserDescription: !!description.trim(),
    });

    // In a real implementation, this would send to a backend/error tracking service
    // For now, we log it and show success
    // Example: await fetch('/api/error-reports', { method: 'POST', body: JSON.stringify(report) });

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleClose = () => {
    setDescription('');
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-[#1a1a24] border border-white/10 rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <BugIcon className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Report Issue</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isSubmitted ? (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 mb-4">
                <CheckIcon className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Thank you for your report!
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                We've logged this issue and will investigate. Your feedback helps us improve.
              </p>
              <p className="text-xs text-slate-500">
                Error ID: <code className="px-1.5 py-0.5 rounded bg-white/5 font-mono">{errorId}</code>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Error Summary */}
              <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
                <p className="text-sm text-rose-400 font-medium mb-1">Error in {feature}</p>
                <p className="text-xs text-slate-400 line-clamp-2">
                  {error?.message || 'An unexpected error occurred'}
                </p>
              </div>

              {/* Description Field */}
              <label className="block mb-4">
                <span className="text-sm font-medium text-slate-300 mb-1.5 block">
                  What were you doing when this happened? <span className="text-slate-500">(optional)</span>
                </span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="I was trying to..."
                  className="w-full h-24 px-3 py-2 text-sm text-white bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 placeholder-slate-500 resize-none"
                />
              </label>

              {/* Error ID Display */}
              <p className="text-xs text-slate-500 mb-4">
                Error ID: <code className="px-1.5 py-0.5 rounded bg-white/5 font-mono">{errorId}</code>
              </p>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Feature Error Boundary
// =============================================================================

export class FeatureErrorBoundary extends React.Component<
  FeatureErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isReportModalOpen: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Generate unique error ID for tracking
    const errorId = generateCorrelationId();
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    const { feature } = this.props;
    const { errorId } = this.state;

    // Log error with full context to logging service
    logger.error(`Error caught in ${feature}`, {
      errorId,
      feature,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Send to Sentry for production error tracking
    captureErrorBoundaryError(error, errorInfo, feature);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    logger.info('User initiated retry', { feature: this.props.feature, errorId: this.state.errorId });
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null, isReportModalOpen: false });
  };

  handleGoHome = (): void => {
    logger.info('User navigated home after error', { feature: this.props.feature, errorId: this.state.errorId });
    window.location.href = '/';
  };

  handleOpenReportModal = (): void => {
    this.setState({ isReportModalOpen: true });
  };

  handleCloseReportModal = (): void => {
    this.setState({ isReportModalOpen: false });
  };

  render(): React.ReactNode {
    const { hasError, error, errorInfo, errorId, isReportModalOpen } = this.state;
    const { feature, fallback, children } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <>
          <Card className="max-w-lg mx-auto my-8" padding="lg">
            <div className="text-center">
              {/* Error Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-500/10 mb-4">
                <AlertTriangleIcon className="w-8 h-8 text-rose-400" />
              </div>

              {/* Error Message */}
              <h2 className="text-xl font-semibold text-white mb-2">
                Something went wrong
              </h2>
              <p className="text-slate-400 mb-2">
                We encountered an error while loading the {feature} feature.
              </p>
              <p className="text-sm text-slate-500 mb-4">
                {error?.message || 'An unexpected error occurred.'}
              </p>

              {/* Error ID for reference */}
              {errorId && (
                <p className="text-xs text-slate-500 mb-6">
                  Error ID: <code className="px-1.5 py-0.5 rounded bg-white/5 font-mono">{errorId}</code>
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button onClick={this.handleRetry}>
                  <RefreshIcon className="w-4 h-4" />
                  Try Again
                </Button>
                <Button variant="secondary" onClick={this.handleGoHome}>
                  <HomeIcon className="w-4 h-4" />
                  Go Home
                </Button>
                <Button variant="secondary" onClick={this.handleOpenReportModal}>
                  <BugIcon className="w-4 h-4" />
                  Report Issue
                </Button>
              </div>

              {/* Error Details (Development Only) */}
              {import.meta.env.DEV && error && (
                <details className="mt-6 text-left">
                  <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-400">
                    Show error details
                  </summary>
                  <pre className="mt-2 p-3 bg-black/50 rounded-lg text-xs text-rose-300 overflow-auto max-h-48">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          </Card>

          {/* Report Issue Modal */}
          <ReportIssueModal
            isOpen={isReportModalOpen}
            onClose={this.handleCloseReportModal}
            error={error}
            errorInfo={errorInfo}
            feature={feature}
            errorId={errorId || 'unknown'}
          />
        </>
      );
    }

    return children;
  }
}

// =============================================================================
// Chart Error Boundary
// =============================================================================

export class ChartErrorBoundary extends React.Component<
  ChartErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null, isReportModalOpen: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = generateCorrelationId();
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    const chartName = this.props.chartName || 'Chart';
    const { errorId } = this.state;

    // Log to logging service
    logger.error(`Chart rendering error: ${chartName}`, {
      errorId,
      chartName,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Send to Sentry for production error tracking
    captureErrorBoundaryError(error, errorInfo, `Chart:${chartName}`);

    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    logger.info('User retried chart render', { chartName: this.props.chartName, errorId: this.state.errorId });
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null, isReportModalOpen: false });
  };

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { chartName = 'Chart', height = 300, children } = this.props;

    if (hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center bg-white/5 rounded-2xl border border-white/10"
          style={{ height: `${height}px` }}
        >
          {/* Error Icon */}
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 mb-3">
            <ChartErrorIcon className="w-6 h-6 text-amber-400" />
          </div>

          {/* Error Message */}
          <p className="text-sm text-slate-400 mb-1">
            Failed to render {chartName}
          </p>
          <p className="text-xs text-slate-500 mb-4 max-w-xs text-center">
            {error?.message || 'The chart encountered an error while rendering.'}
          </p>

          {/* Retry Button */}
          <button
            onClick={this.handleRetry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-cyan-400 bg-cyan-400/10 rounded-lg hover:bg-cyan-400/20 transition-colors"
          >
            <RefreshIcon className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      );
    }

    return children;
  }
}

// =============================================================================
// Import Error Boundary
// =============================================================================

export interface ImportErrorBoundaryProps {
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Callback when user wants to retry */
  onRetry?: () => void;
  /** Children to render */
  children: React.ReactNode;
}

export class ImportErrorBoundary extends React.Component<
  ImportErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ImportErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null, isReportModalOpen: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = generateCorrelationId();
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    const { errorId } = this.state;

    // Log to logging service
    logger.error('File import error', {
      errorId,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Send to Sentry for production error tracking
    captureErrorBoundaryError(error, errorInfo, 'FileImport');

    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    logger.info('User retried file import', { errorId: this.state.errorId });
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null, isReportModalOpen: false });
    this.props.onRetry?.();
  };

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className="p-6 bg-rose-500/10 border border-rose-500/20 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <AlertTriangleIcon className="w-6 h-6 text-rose-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-rose-400 mb-1">
                Import Error
              </h3>
              <p className="text-sm text-slate-400 mb-3">
                {error?.message || 'Failed to process the uploaded file.'}
              </p>
              <p className="text-xs text-slate-500 mb-4">
                Please check your file format and try again. Make sure the file
                is a valid CSV with the required columns.
              </p>
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-rose-500/20 rounded-lg hover:bg-rose-500/30 transition-colors"
              >
                <RefreshIcon className="w-3.5 h-3.5" />
                Try Another File
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

// =============================================================================
// App-Level Error Boundary
// =============================================================================

export interface AppErrorBoundaryProps {
  /** Children to render */
  children: React.ReactNode;
  /** Custom fallback UI (optional) */
  fallback?: React.ReactNode;
}

/**
 * Top-level error boundary for the entire application.
 * Catches unhandled errors and provides a full-page recovery UI.
 */
export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isReportModalOpen: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = generateCorrelationId();
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    const { errorId } = this.state;

    // Log critical app-level error
    logger.error('Critical application error', {
      errorId,
      feature: 'Application',
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Send to Sentry for production error tracking
    captureErrorBoundaryError(error, errorInfo, 'Application');
  }

  handleReload = (): void => {
    logger.info('User reloaded app after error', { errorId: this.state.errorId });
    window.location.reload();
  };

  handleGoHome = (): void => {
    logger.info('User navigated home after app error', { errorId: this.state.errorId });
    window.location.href = '/';
  };

  handleOpenReportModal = (): void => {
    this.setState({ isReportModalOpen: true });
  };

  handleCloseReportModal = (): void => {
    this.setState({ isReportModalOpen: false });
  };

  render(): React.ReactNode {
    const { hasError, error, errorInfo, errorId, isReportModalOpen } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <>
          <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
            <div className="max-w-lg w-full">
              <Card padding="lg">
                <div className="text-center">
                  {/* Error Icon */}
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-rose-500/10 mb-6">
                    <AlertTriangleIcon className="w-10 h-10 text-rose-400" />
                  </div>

                  {/* Error Message */}
                  <h1 className="text-2xl font-bold text-white mb-3">
                    Something went wrong
                  </h1>
                  <p className="text-slate-400 mb-2">
                    We're sorry, but something unexpected happened.
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    {error?.message || 'An unexpected error occurred.'}
                  </p>

                  {/* Error ID */}
                  {errorId && (
                    <p className="text-xs text-slate-500 mb-6">
                      Error ID: <code className="px-1.5 py-0.5 rounded bg-white/5 font-mono">{errorId}</code>
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button onClick={this.handleReload}>
                      <RefreshIcon className="w-4 h-4" />
                      Reload App
                    </Button>
                    <Button variant="secondary" onClick={this.handleGoHome}>
                      <HomeIcon className="w-4 h-4" />
                      Go Home
                    </Button>
                    <Button variant="secondary" onClick={this.handleOpenReportModal}>
                      <BugIcon className="w-4 h-4" />
                      Report Issue
                    </Button>
                  </div>

                  {/* Error Details (Development Only) */}
                  {import.meta.env.DEV && error && (
                    <details className="mt-6 text-left">
                      <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-400">
                        Show error details
                      </summary>
                      <pre className="mt-2 p-3 bg-black/50 rounded-lg text-xs text-rose-300 overflow-auto max-h-48">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              </Card>

              {/* Footer hint */}
              <p className="text-center text-xs text-slate-600 mt-6">
                If this problem persists, please contact support with the error ID above.
              </p>
            </div>
          </div>

          {/* Report Issue Modal */}
          <ReportIssueModal
            isOpen={isReportModalOpen}
            onClose={this.handleCloseReportModal}
            error={error}
            errorInfo={errorInfo}
            feature="Application"
            errorId={errorId || 'unknown'}
          />
        </>
      );
    }

    return children;
  }
}

// =============================================================================
// Higher-Order Component for Wrapping Pages
// =============================================================================

/**
 * HOC to wrap a page component with a feature error boundary
 */
export function withFeatureErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  feature: string
): React.FC<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const WithErrorBoundary: React.FC<P> = (props) => (
    <FeatureErrorBoundary feature={feature}>
      <WrappedComponent {...props} />
    </FeatureErrorBoundary>
  );

  WithErrorBoundary.displayName = `withFeatureErrorBoundary(${displayName})`;

  return WithErrorBoundary;
}

/**
 * HOC to wrap a chart component with a chart error boundary
 */
export function withChartErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  chartName: string,
  height?: number
): React.FC<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const WithErrorBoundary: React.FC<P> = (props) => (
    <ChartErrorBoundary chartName={chartName} height={height}>
      <WrappedComponent {...props} />
    </ChartErrorBoundary>
  );

  WithErrorBoundary.displayName = `withChartErrorBoundary(${displayName})`;

  return WithErrorBoundary;
}

// =============================================================================
// Exports
// =============================================================================

export default FeatureErrorBoundary;
